/**
 * Builds a stable GroupContext from the enriched group array returned by
 * getGroupsRatings (after client-side enrichment in useGroupData).
 *
 * GroupContext is the single source of truth for:
 *   - Who owns the save for each student (gradingProfileId + gradingCriteria)
 *   - What permission level each student has (EDIT / VIEW / own)
 *   - Which shared-group criteria keys are reachable (accessibleCriteriaIds)
 *
 * @param {Object} params
 * @param {Array}       params.enrichedGroups
 *   The enriched array from getGroupsRatings. Each group:
 *   {
 *     groupId, shareGroupId, permission, gradingProfileId,
 *     sharedGradingCriteria, gradingProfileStatus,
 *     groupStudentContents: [{ gradingProfileStudentId, ... }]
 *   }
 *   Own groups have shareGroupId: null, permission: null.
 *
 * @param {Object|null} params.effectiveEpr
 *   The teacher's own EPR (may be null for shared-only teachers).
 *   Used to derive gradingCriteriaId for own-group students.
 *
 * @param {number}      params.templateId
 *   The selected template's templateId, used to look up the own
 *   gradingCriteriaId from effectiveEpr.gradingCriteriaList.
 *
 * @returns {GroupContext} {
 *   ownProfile: {
 *     gradingProfileId: string,
 *     gradingCriteriaId: number | null,
 *     eprStatus: string,
 *     homeroomGroupIds: number[],
 *     studentIds: number[]
 *   } | null,
 *   sharedGroups: [{
 *     groupId: number,
 *     shareGroupId: number,
 *     sharedGradingProfile: number,
 *     sharedGradingCriteria: number,
 *     eprStatus: string,
 *     permission: "EDIT" | "VIEW",
 *     studentIds: number[]
 *   }],
 *   accessibleCriteriaIds: Set<number>
 *     criteriaKeys reachable via any shared group (EDIT or VIEW).
 * }
 */
const buildGroupContext = ({ enrichedGroups, effectiveEpr, templateId }) => {
    const ownGroups = (enrichedGroups ?? []).filter((g) => !g.shareGroupId);
    const sharedGroupsRaw = (enrichedGroups ?? []).filter((g) => !!g.shareGroupId);

    // ── Derive own gradingCriteriaId ───────────────────────────────────────────
    const ownGradingCriteria = effectiveEpr?.gradingCriteriaList?.find(
        (c) => Number.parseInt(c.parentTemplate, 10) === templateId
    );
    const ownGradingCriteriaId = ownGradingCriteria?.key ?? null;

    // ── Build ownProfile ───────────────────────────────────────────────────────
    let ownProfile = null;
    if (effectiveEpr || ownGroups.length > 0) {
        const homeroomGroupIds = ownGroups.map((g) => g.groupId);
        const studentIds = ownGroups.flatMap((g) =>
            (g.groupStudentContents ?? []).map((s) => s.gradingProfileStudentId)
        );

        ownProfile = {
            gradingProfileId: effectiveEpr?.key ?? null,
            gradingCriteriaId: ownGradingCriteriaId ? Number(ownGradingCriteriaId) : null,
            eprStatus: effectiveEpr?.status ?? null,
            homeroomGroupIds,
            studentIds
        };
    }

    // ── Build sharedGroups ─────────────────────────────────────────────────────
    // sharedGradingCriteria and gradingProfileStatus are set by the enrichment
    // step in useGroupData.fetchSelectedGroupInfo before being stored in state.
    const sharedGroups = sharedGroupsRaw.map((g) => ({
        groupId: g.groupId,
        shareGroupId: g.shareGroupId,
        sharedGradingProfile: g.gradingProfileId,
        sharedGradingCriteria: g.sharedGradingCriteria,
        eprStatus: g.gradingProfileStatus ?? null,
        // permission: null on own groups is handled above; shared groups always have
        // an explicit value. Treat any non-EDIT value as VIEW for safety.
        permission: g.permission === "EDIT" ? "EDIT" : "VIEW",
        studentIds: (g.groupStudentContents ?? []).map((s) => s.gradingProfileStudentId)
    }));

    // ── accessibleCriteriaIds ─────────────────────────────────────────────────
    const accessibleCriteriaIds = new Set(sharedGroups.map((sg) => sg.sharedGradingCriteria).filter(Boolean));

    return { ownProfile, sharedGroups, accessibleCriteriaIds };
};

export default buildGroupContext;
